// useIframeCapture.js
import { useCallback, useRef } from 'react';

/**
 * Starts a getDisplayMedia capture and returns:
 *   - a <video> element playing the display stream
 *   - a <canvas> cropped to the iframe's bounding rect (at devicePixelRatio)
 *   - a draw loop that keeps the canvas in sync
 *   - a stop() function that tears everything down
 */
export function useIframeCapture(iframeRef) {
    const streamRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const rafRef = useRef(0);
    const drawingRef = useRef(false);

    const start = useCallback(async ({ scale = 1 } = {}) => {
        if (!iframeRef.current) throw new Error('iframe not mounted');

        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: { frameRate: 60 },
            audio: false,
            preferCurrentTab: true,
            selfBrowserSurface: 'include',
            surfaceSwitching: 'exclude',
        });
        streamRef.current = stream;

        const video = document.createElement('video');
        video.srcObject = stream;
        video.muted = true;
        await video.play();
        videoRef.current = video;

        // Wait until the video knows its real dimensions.
        if (!video.videoWidth || !video.videoHeight) {
            await new Promise(res => {
                video.onloadedmetadata = () => res();
            });
        }

        // Give the browser a couple of frames to finish reflowing after the
        // "sharing this tab" banner pushes the viewport down.
        const SETTLE_MS = 500;
        await new Promise(r => setTimeout(r, SETTLE_MS));

        // Then wait for a stable layout: poll innerHeight until it stops changing.
        await new Promise(resolve => {
            let lastH = window.innerHeight;
            let stableFrames = 0;
            const check = () => {
                const h = window.innerHeight;
                if (h === lastH) {
                    stableFrames++;
                    if (stableFrames >= 3) return resolve();
                } else {
                    stableFrames = 0;
                    lastH = h;
                }
                requestAnimationFrame(check);
            };
            requestAnimationFrame(check);
        });

        // NOW measure the iframe, in the post-banner layout.
        const rect = iframeRef.current.getBoundingClientRect();

        // The captured video's pixel size corresponds to the *current* viewport.
        // Deriving the scale this way is immune to DPR surprises and to the banner.
        const scaleX = video.videoWidth / window.innerWidth;
        const scaleY = video.videoHeight / window.innerHeight;

        const sx = Math.round(rect.left * scaleX);
        const sy = Math.round(rect.top * scaleY);
        const sw = Math.round(rect.width * scaleX);
        const sh = Math.round(rect.height * scaleY);

        const dw = Math.round(sw * scale);
        const dh = Math.round(sh * scale);

        const canvas = document.createElement('canvas');
        canvas.width = dw;
        canvas.height = dh;
        canvasRef.current = canvas;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        drawingRef.current = true;
        const draw = () => {
            if (!drawingRef.current) return;
            ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dw, dh);
            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);

        return { video, canvas, ctx, sw: dw, sh: dh, stream };
    }, [iframeRef]);

    const stop = useCallback(() => {
        drawingRef.current = false;
        cancelAnimationFrame(rafRef.current);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        videoRef.current = null;
        canvasRef.current = null;
    }, []);

    return { start, stop };
}